// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  TagEntity,
  TagAssignmentEntity,
} from '@shared/data-access/interfaces/redux/tags-management-state-types';
import {
  tagsAdapter,
  assignmentsAdapter,
  generateAssignmentId,
  initialState,
} from './tags-management-state';
import { tagsApi } from '@shared/data-access/api/engine/tags-api';

export const tagsManagementSlice = createSlice({
  initialState: () => initialState,
  name: 'tagsManagement',
  reducers: {
    reset: () => initialState,

    upsertTags: (state, { payload }: PayloadAction<ReadonlyArray<TagEntity>>) => {
      tagsAdapter.upsertMany(state.tags, payload);
    },

    upsertAssignments: (
      state,
      { payload }: PayloadAction<ReadonlyArray<TagAssignmentEntity>>,
    ) => {
      assignmentsAdapter.upsertMany(state.assignments, payload);
    },
  },
  extraReducers: builder => {
    return builder
      .addMatcher(
        tagsApi.endpoints.getTags.matchFulfilled,
        (state, { payload }) => {
          tagsAdapter.upsertMany(state.tags, payload.data);
        },
      )
      .addMatcher(
        tagsApi.endpoints.syncTags.matchFulfilled,
        (state, { payload }) => {
          const assignmentId = generateAssignmentId(
            payload.object_type,
            payload.object_id,
          );

          if (payload.tags.length === 0) {
            assignmentsAdapter.removeOne(state.assignments, assignmentId);

            return;
          }

          assignmentsAdapter.upsertOne(state.assignments, {
            object_type: payload.object_type,
            object_id: payload.object_id,
            tag_ids: payload.tags.map(tag => tag.tag_id),
          });
        },
      )
      .addMatcher(
        tagsApi.endpoints.searchTagAssignments.matchFulfilled,
        (state, { payload }) => {
          const entities = payload.data.map(item => ({
            object_type: item.object_type,
            object_id: item.object_id,
            tag_ids: item.tags.map(tag => tag.tag_id),
          }));

          // Search by owner_type is the full assignment set; replace so cleared
          // objects (omitted from the response) do not linger in persisted state.
          assignmentsAdapter.setAll(state.assignments, entities);
        },
      );
  },
});
